import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { dropRight, size } from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import { Button, Typography } from '@material-ui/core';
import messages from '../messages';

const S = {
  Wrapper: styled.div`
    display: flex;
    flex-direction: column;
  `,
  Instruction: styled(Typography)`
    margin-bottom: 2rem;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    font-weight: 700;
  `,
  Row: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    order: -1;
    margin-bottom: 1.5rem;
  `,
  Info: styled(Typography)`
    margin-right: auto;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(12)};
  `,
  Number: styled.span`
    margin-right: 4px;
  `,
  Type: styled.span`
    margin-left: 4px;
  `,
  Actions: styled.div`
    display: flex;
    justify-content: flex-end;
  `,
  Button: styled(Button)`
    min-width: 0;
    min-height: 32px;
    padding: 3px 8px;
    :not(:first-child) {
      margin-left: 8px;
    }
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    :disabled {
      color: ${({ theme }) => theme.palette.primary.contrastText};
    }
    ${({ variant, theme }) => variant === 'outlined' && css`
      color: ${theme.palette.grey[600]};
    `}
  `,
};

const AddSignaturePaper = ({
  assigned, setAssigned, finishStep, setFieldValue, signatures, setStep, setType,
}) => {
  const goPrevStep = useCallback(() => {
    if (assigned) {
      setFieldValue('signatures', dropRight(1, signatures));
    }

    setAssigned(false);

    setStep(prevStep => prevStep - 1);

    setType(null);
  }, [assigned, setAssigned, setFieldValue, setStep, setType, signatures]);

  return (
    <S.Wrapper>
      <S.Instruction>
        <FormattedMessage {...messages.addSignaturePaperInstruction} />
      </S.Instruction>
      <S.Row>
        <S.Info>
          <S.Number>
            <FormattedMessage {...messages.addSignaturePaperNumber} values={{ number: assigned ? size(signatures) : size(signatures) + 1 }} />
          </S.Number>
          /
          <S.Type>
            <FormattedMessage {...messages.addSignaturePaperType} />
          </S.Type>
        </S.Info>
      </S.Row>
      <S.Actions>
        <S.Button type="button" variant="outlined" onClick={goPrevStep}>
          <FormattedMessage {...messages.addSignaturePaperButtonPrev} />
        </S.Button>
        <S.Button type="button" color="primary" variant="contained" disabled={!assigned} onClick={finishStep}>
          <FormattedMessage {...messages.addSignaturePaperButtonNext} />
        </S.Button>
      </S.Actions>
    </S.Wrapper>
  );
};

AddSignaturePaper.propTypes = {
  assigned: PropTypes.bool.isRequired,
  finishStep: PropTypes.func.isRequired,
  setAssigned: PropTypes.func.isRequired,
  signatures: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.number.isRequired,
      position: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ),
  setFieldValue: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
  setType: PropTypes.func.isRequired,
};

AddSignaturePaper.defaultProps = {
  signatures: null,
};

export default AddSignaturePaper;
